import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import CustomTimes from '../../components/CustomTimes';
import CustomText from '../../components/CustomText';
import BACKGROUND_IMAGES from './BackgroundImages';

// Canvas represents actual display size
const DISPLAY_WIDTH = 1920;
const DISPLAY_HEIGHT = 1080;
const HEADER_HEIGHT = 84; // Matches display: padding + content height
const FOOTER_HEIGHT = 36; // Footer: 10px padding top/bottom + 14px text + 2px border
const PADDING = 20;

// Calculate available content area (what's left for boxes)
const CONTENT_WIDTH = DISPLAY_WIDTH - (PADDING * 2);
const CONTENT_HEIGHT = DISPLAY_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT - (PADDING * 2);

const DisplayLayoutBuilderV3 = () => {
  const { layoutId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  // Core state
  const [boxes, setBoxes] = useState([]);
  const [selectedBoxId, setSelectedBoxId] = useState(null);
  const [globalSettings, setGlobalSettings] = useState({
    backgroundType: 'default',
    backgroundColor: '#000000',
    backgroundImage: null,
    headerBgColor: '#162A45',
    headerTextColor: '#ffc764',
    boxesOutlineColor: '#d4af37',
    boxesBackgroundColor: 'transparent'
  });

  // History state for undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedoAction, setIsUndoRedoAction] = useState(false);
  const historyTimeoutRef = useRef(null);

  // Available content items
  const [availableItems, setAvailableItems] = useState({
    zmanim: [],
    limudim: [],
    calendar: [],
    customTimes: [],
    customTexts: []
  });

  // UI state
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('saved');
  const [showItemModal, setShowItemModal] = useState(false);
  const [dragState, setDragState] = useState(null);
  const [canvasScale, setCanvasScale] = useState(1);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const GRID_SIZE = 20; // Grid snap size in pixels

  // Custom Times/Text modal state
  const [isCustomModal, setIsCustomModal] = useState(false);
  const [customModalType, setCustomModalType] = useState('time'); // 'time' or 'text'
  const [editingCustomTime, setEditingCustomTime] = useState(null);
  const [editingCustomText, setEditingCustomText] = useState(null);

  // Load layout on mount
  useEffect(() => {
    if (!layoutId) {
      navigate('/manage/display-layouts');
      return;
    }
    loadLayout();
    loadAvailableItems();
  }, [layoutId]);

  // Calculate canvas scale to fit viewport
  useEffect(() => {
    const calculateScale = () => {
      const viewportWidth = window.innerWidth - 520; // Subtract sidebars
      const viewportHeight = window.innerHeight - 180; // Subtract header/padding
      const scaleX = viewportWidth / DISPLAY_WIDTH;
      const scaleY = viewportHeight / DISPLAY_HEIGHT;
      setCanvasScale(Math.min(scaleX, scaleY, 1));
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  // Track history when boxes or settings change (but not during undo/redo)
  // Debounced to avoid creating history entries for every pixel during dragging
  useEffect(() => {
    if (!loading && !isUndoRedoAction) {
      // Clear any pending history update
      if (historyTimeoutRef.current) {
        clearTimeout(historyTimeoutRef.current);
      }

      // Wait 500ms after the last change before adding to history
      historyTimeoutRef.current = setTimeout(() => {
        const newState = { boxes: JSON.parse(JSON.stringify(boxes)), globalSettings: JSON.parse(JSON.stringify(globalSettings)) };

        // Don't add to history if it's the same as the current state
        if (historyIndex >= 0 && JSON.stringify(history[historyIndex]) === JSON.stringify(newState)) {
          return;
        }

        // Remove any future history if we're not at the end
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newState);

        // Limit history to 50 states
        if (newHistory.length > 50) {
          newHistory.shift();
        } else {
          setHistoryIndex(historyIndex + 1);
        }

        setHistory(newHistory);
      }, 500);
    }
    setIsUndoRedoAction(false);

    return () => {
      if (historyTimeoutRef.current) {
        clearTimeout(historyTimeoutRef.current);
      }
    };
  }, [boxes, globalSettings, loading]);

  // Auto-save when boxes or settings change
  useEffect(() => {
    if (!loading && layoutId) {
      setSaveStatus('saving');
      const timer = setTimeout(async () => {
        try {
          await api.put(`/shul/display-layouts/${layoutId}/`, {
            layout_config: {
              version: 'v2',
              boxes,
              globalSettings
            }
          });
          setSaveStatus('saved');
        } catch (error) {
          console.error('Save error:', error);
          setSaveStatus('error');
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [boxes, globalSettings, loading, layoutId]);

  const loadLayout = async () => {
    try {
      const response = await api.get(`/shul/display-layouts/${layoutId}/`);
      if (response.layout_config?.version === 'v2') {
        let boxes = response.layout_config.boxes || [];

        // Ensure memorial boxes exist - add them if missing
        const hasIluiBox = boxes.some(b => b.id === 'memorial_ilui_nishmat');
        const hasRefuahBox = boxes.some(b => b.id === 'memorial_refuah_shleima');

        if (!hasIluiBox || !hasRefuahBox) {
          // Import createMemorialBoxes dynamically
          const { createMemorialBoxes } = await import('./LayoutTemplates');
          const memorialBoxes = createMemorialBoxes();

          if (!hasIluiBox) {
            boxes.push(memorialBoxes[0]);
          }
          if (!hasRefuahBox) {
            boxes.push(memorialBoxes[1]);
          }
        }

        setBoxes(boxes);
        setGlobalSettings(response.layout_config.globalSettings || globalSettings);
      }
      setLoading(false);
    } catch (error) {
      console.error('Load error:', error);
      alert('Failed to load layout');
      navigate('/manage/display-layouts');
    }
  };

  const loadAvailableItems = async () => {
    try {
      const zmanimRes = await api.get('/zmanim/');
      const customTimesRes = await api.get('/custom-times/');
      const customTextsRes = await api.get('/custom-texts/');

      setAvailableItems({
        zmanim: Object.keys(zmanimRes.zmanim || {}).map(key => ({
          id: `zmanim_${key}`,
          name: key,
          category: 'zmanim'
        })),
        limudim: Object.keys(zmanimRes.limudim || {}).map(key => ({
          id: `limudim_${key}`,
          name: key,
          category: 'limudim'
        })),
        calendar: Object.keys(zmanimRes.jewish_calendar || {}).map(key => ({
          id: `calendar_${key}`,
          name: key,
          category: 'calendar'
        })),
        customTimes: (customTimesRes || []).map(ct => ({
          id: ct.internal_name,
          name: ct.display_name,
          category: 'custom_time'
        })),
        customTexts: (customTextsRes || []).map(ct => ({
          id: `customtext_${ct.internal_name}`,
          name: ct.display_name,
          category: 'custom_text'
        }))
      });
    } catch (error) {
      console.error('Error loading items:', error);
    }
  };

  // Undo/Redo functions
  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      setIsUndoRedoAction(true);
      setBoxes(JSON.parse(JSON.stringify(previousState.boxes)));
      setGlobalSettings(JSON.parse(JSON.stringify(previousState.globalSettings)));
      setHistoryIndex(newIndex);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      setIsUndoRedoAction(true);
      setBoxes(JSON.parse(JSON.stringify(nextState.boxes)));
      setGlobalSettings(JSON.parse(JSON.stringify(nextState.globalSettings)));
      setHistoryIndex(newIndex);
    }
  };

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [historyIndex, history]);

  // Box operations
  const createBox = () => {
    const newBox = {
      id: `box_${Date.now()}`,
      displayName: 'New Box',
      position: { x: 100, y: 100, width: 300, height: 400 },
      styling: {
        showBorder: true,
        borderColor: '#d4af37',
        backgroundColor: 'transparent',
        titleFont: 'Arial',
        titleColor: '#ffc764',
        titleSize: 24,
        textFont: 'Arial',
        textColor: '#ffffff',
        textSize: 22,
        highlightEnabled: false,
        highlightType: 'glow',
        highlightColor: '#00ff00'
      },
      items: [],
      showOnDisplay: true
    };
    setBoxes([...boxes, newBox]);
    setSelectedBoxId(newBox.id);
  };

  const deleteBox = () => {
    if (!selectedBoxId) return;

    const box = boxes.find(b => b.id === selectedBoxId);

    // Prevent deletion of memorial boxes
    if (box?.isMemorialBox) {
      alert('Memorial boxes (לעילוי נשמת and רפואה שלמה) cannot be deleted. You can resize and reposition them.');
      return;
    }

    if (!window.confirm('Delete this box?')) return;
    setBoxes(boxes.filter(b => b.id !== selectedBoxId));
    setSelectedBoxId(null);
  };

  const updateBox = (boxId, updates) => {
    setBoxes(boxes.map(b => b.id === boxId ? { ...b, ...updates } : b));
  };

  const updateBoxStyling = (field, value) => {
    if (!selectedBoxId) return;
    updateBox(selectedBoxId, {
      styling: { ...selectedBox.styling, [field]: value }
    });
  };

  // Canvas coordinate conversion
  const screenToCanvas = (clientX, clientY) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (clientX - rect.left) / canvasScale - PADDING;
    const y = (clientY - rect.top) / canvasScale - HEADER_HEIGHT - PADDING;
    return { x: Math.round(x), y: Math.round(y) };
  };

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  // Snap value to grid
  const snapToGridValue = (value) => {
    if (!snapToGrid) return value;
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
  };

  // Check if two boxes overlap
  const boxesOverlap = (box1Pos, box2Pos) => {
    return !(
      box1Pos.x + box1Pos.width <= box2Pos.x ||
      box2Pos.x + box2Pos.width <= box1Pos.x ||
      box1Pos.y + box1Pos.height <= box2Pos.y ||
      box2Pos.y + box2Pos.height <= box1Pos.y
    );
  };

  // Check if position would overlap with any memorial boxes
  const wouldOverlapMemorialBox = (boxId, newPosition) => {
    const memorialBoxes = boxes.filter(b => b.isMemorialBox && b.id !== boxId);
    return memorialBoxes.some(mb => boxesOverlap(newPosition, mb.position));
  };

  // Drag and resize handlers
  const handleMouseDown = (e, boxId, mode, direction = null) => {
    e.stopPropagation();
    setSelectedBoxId(boxId);

    const box = boxes.find(b => b.id === boxId);
    if (!box) return;

    const { x, y } = screenToCanvas(e.clientX, e.clientY);
    setDragState({
      boxId,
      mode,
      direction,
      startX: x,
      startY: y,
      startPos: { ...box.position }
    });
  };

  const handleMouseMove = (e) => {
    if (!dragState) return;

    const { x, y } = screenToCanvas(e.clientX, e.clientY);
    const dx = x - dragState.startX;
    const dy = y - dragState.startY;

    const box = boxes.find(b => b.id === dragState.boxId);
    if (!box) return;

    let newPos = { ...dragState.startPos };

    if (dragState.mode === 'move') {
      newPos.x = snapToGridValue(clamp(dragState.startPos.x + dx, 0, CONTENT_WIDTH - newPos.width));
      newPos.y = snapToGridValue(clamp(dragState.startPos.y + dy, 0, CONTENT_HEIGHT - newPos.height));
    } else if (dragState.mode === 'resize') {
      const dir = dragState.direction;

      // Calculate new dimensions
      if (dir.includes('e')) newPos.width = dragState.startPos.width + dx;
      if (dir.includes('w')) {
        newPos.width = dragState.startPos.width - dx;
        newPos.x = dragState.startPos.x + dx;
      }
      if (dir.includes('s')) newPos.height = dragState.startPos.height + dy;
      if (dir.includes('n')) {
        newPos.height = dragState.startPos.height - dy;
        newPos.y = dragState.startPos.y + dy;
      }

      // Enforce minimum size (use memorial box minimums if applicable)
      const minWidth = box.minWidth || 100;
      const minHeight = box.minHeight || 100;

      if (newPos.width < minWidth) {
        newPos.width = minWidth;
        if (dir.includes('w')) newPos.x = dragState.startPos.x + dragState.startPos.width - minWidth;
      }
      if (newPos.height < minHeight) {
        newPos.height = minHeight;
        if (dir.includes('n')) newPos.y = dragState.startPos.y + dragState.startPos.height - minHeight;
      }

      // Apply grid snap to position and size
      newPos.x = snapToGridValue(newPos.x);
      newPos.y = snapToGridValue(newPos.y);
      newPos.width = snapToGridValue(newPos.width);
      newPos.height = snapToGridValue(newPos.height);

      // Enforce bounds after snapping
      newPos.x = clamp(newPos.x, 0, CONTENT_WIDTH - newPos.width);
      newPos.y = clamp(newPos.y, 0, CONTENT_HEIGHT - newPos.height);
      newPos.width = clamp(newPos.width, minWidth, CONTENT_WIDTH - newPos.x);
      newPos.height = clamp(newPos.height, minHeight, CONTENT_HEIGHT - newPos.y);
    }

    // Prevent non-memorial boxes from overlapping memorial boxes
    if (!box.isMemorialBox && wouldOverlapMemorialBox(dragState.boxId, newPos)) {
      // Don't update position if it would overlap a memorial box
      return;
    }

    updateBox(dragState.boxId, { position: newPos });
  };

  const handleMouseUp = () => {
    setDragState(null);
  };

  // Setup mouse event listeners
  useEffect(() => {
    if (!dragState) return;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState]);

  // Item management
  const addItemsToBox = (items) => {
    if (!selectedBoxId) return;
    const box = boxes.find(b => b.id === selectedBoxId);
    if (!box) return;

    const existingIds = box.items.map(i => i.id);
    const newItems = items
      .filter(item => !existingIds.includes(item.id))
      .map(item => ({ id: item.id, name: item.name }));

    updateBox(selectedBoxId, { items: [...box.items, ...newItems] });
    setShowItemModal(false);
  };

  const removeItem = (boxId, itemId) => {
    const box = boxes.find(b => b.id === boxId);
    if (!box) return;
    updateBox(boxId, { items: box.items.filter(i => i.id !== itemId) });
  };

  const moveItem = (boxId, fromIndex, toIndex) => {
    const box = boxes.find(b => b.id === boxId);
    if (!box) return;
    const items = [...box.items];
    const [moved] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, moved);
    updateBox(boxId, { items });
  };

  // Custom Time/Text handlers
  const handleAddCustomTime = () => {
    setEditingCustomTime(null);
    setCustomModalType('time');
    setIsCustomModal(true);
  };

  const handleAddCustomText = () => {
    setEditingCustomText(null);
    setCustomModalType('text');
    setIsCustomModal(true);
  };

  const handleCustomTimeCreated = () => {
    loadAvailableItems(); // Refresh the items list
    setEditingCustomTime(null);
    setIsCustomModal(false);
  };

  const handleCustomTextCreated = () => {
    loadAvailableItems(); // Refresh the items list
    setEditingCustomText(null);
    setIsCustomModal(false);
  };

  const handleCancelCustomEdit = () => {
    setEditingCustomTime(null);
    setEditingCustomText(null);
    setIsCustomModal(false);
  };

  // Edit custom time handler
  const handleEditCustomTime = async (internalName) => {
    try {
      // Fetch the full custom time data from API to get the real database ID
      const customTimesRes = await api.get('/custom-times/');
      const customTime = customTimesRes.find(ct => ct.internal_name === internalName);

      if (customTime) {
        setEditingCustomTime(customTime);
        setCustomModalType('time');
        setIsCustomModal(true);
      }
    } catch (error) {
      console.error('Error fetching custom time for edit:', error);
      alert('Error loading custom time for editing');
    }
  };

  // Edit custom text handler
  const handleEditCustomText = async (internalName) => {
    try {
      const cleanInternalName = internalName.replace('customtext_', '');
      // Fetch the full custom text data from API to get the real database ID
      const customTextsRes = await api.get('/custom-texts/');
      const customText = customTextsRes.find(ct => ct.internal_name === cleanInternalName);

      if (customText) {
        setEditingCustomText(customText);
        setCustomModalType('text');
        setIsCustomModal(true);
      }
    } catch (error) {
      console.error('Error fetching custom text for edit:', error);
      alert('Error loading custom text for editing');
    }
  };

  // Delete custom time handler
  const handleDeleteCustomTime = async (internalName) => {
    const customTime = availableItems.customTimes.find(ct =>
      ct.id === internalName || ct.id === `customtime_${internalName}`
    );
    if (!customTime) return;

    // Check which boxes are using this custom time
    const usedInBoxes = boxes.filter(box =>
      box.items.some(item => item.id === internalName || item.id === `customtime_${internalName}`)
    ).map(box => box.displayName);

    // Build confirmation message
    let confirmMessage = `Are you sure you want to delete "${customTime.name}"?`;
    if (usedInBoxes.length > 0) {
      confirmMessage += `\n\nWARNING: This custom time is currently being used in the following boxes:\n- ${usedInBoxes.join('\n- ')}`;
      confirmMessage += '\n\nDeleting it will remove it from these boxes.';
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Get the actual ID from the API
      const customTimesRes = await api.get('/custom-times/');
      const fullCustomTime = customTimesRes.find(ct => ct.internal_name === internalName);

      if (fullCustomTime) {
        await api.delete(`/custom-times/${fullCustomTime.id}/`);

        // Remove from any boxes that were using it
        if (usedInBoxes.length > 0) {
          setBoxes(prevBoxes =>
            prevBoxes.map(box => ({
              ...box,
              items: box.items.filter(item =>
                item.id !== internalName && item.id !== `customtime_${internalName}`
              )
            }))
          );
        }

        // Refresh available items
        loadAvailableItems();
      }
    } catch (error) {
      console.error('Error deleting custom time:', error);
      alert('Error deleting custom time: ' + (error.message || 'Unknown error'));
    }
  };

  // Delete custom text handler
  const handleDeleteCustomText = async (internalName) => {
    const cleanInternalName = internalName.replace('customtext_', '');
    const customText = availableItems.customTexts.find(ct =>
      ct.id === `customtext_${cleanInternalName}` || ct.id === cleanInternalName
    );
    if (!customText) return;

    // Check which boxes are using this custom text
    const usedInBoxes = boxes.filter(box =>
      box.items.some(item =>
        item.id === `customtext_${cleanInternalName}` || item.id === cleanInternalName
      )
    ).map(box => box.displayName);

    // Build confirmation message
    let confirmMessage = `Are you sure you want to delete "${customText.name}"?`;
    if (usedInBoxes.length > 0) {
      confirmMessage += `\n\nWARNING: This custom text is currently being used in the following boxes:\n- ${usedInBoxes.join('\n- ')}`;
      confirmMessage += '\n\nDeleting it will remove it from these boxes.';
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Get the actual ID from the API
      const customTextsRes = await api.get('/custom-texts/');
      const fullCustomText = customTextsRes.find(ct => ct.internal_name === cleanInternalName);

      if (fullCustomText) {
        await api.delete(`/custom-texts/${fullCustomText.id}/`);

        // Remove from any boxes that were using it
        if (usedInBoxes.length > 0) {
          setBoxes(prevBoxes =>
            prevBoxes.map(box => ({
              ...box,
              items: box.items.filter(item =>
                item.id !== `customtext_${cleanInternalName}` && item.id !== cleanInternalName
              )
            }))
          );
        }

        // Refresh available items
        loadAvailableItems();
      }
    } catch (error) {
      console.error('Error deleting custom text:', error);
      alert('Error deleting custom text: ' + (error.message || 'Unknown error'));
    }
  };

  const selectedBox = boxes.find(b => b.id === selectedBoxId);

  if (loading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  return (
    <div style={styles.container}>
      {/* Toolbar */}
      <div style={styles.toolbar}>
        <div>
          <h1 style={styles.title}>Display Builder</h1>
          <span style={styles.saveStatus}>
            {saveStatus === 'saving' && 'Saving...'}
            {saveStatus === 'saved' && '✓ Saved'}
            {saveStatus === 'error' && '✗ Error'}
          </span>
        </div>
        <div style={styles.toolbarButtons}>
          <button
            onClick={undo}
            disabled={historyIndex <= 0}
            style={{
              ...styles.btnSecondary,
              opacity: historyIndex <= 0 ? 0.5 : 1,
              cursor: historyIndex <= 0 ? 'not-allowed' : 'pointer'
            }}
            title="Undo (Ctrl+Z)"
          >
            ↶ Undo
          </button>
          <button
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
            style={{
              ...styles.btnSecondary,
              opacity: historyIndex >= history.length - 1 ? 0.5 : 1,
              cursor: historyIndex >= history.length - 1 ? 'not-allowed' : 'pointer'
            }}
            title="Redo (Ctrl+Y)"
          >
            ↷ Redo
          </button>
          <button onClick={() => navigate('/manage/display-layouts')} style={styles.btnSecondary}>
            Back
          </button>
          <button onClick={createBox} style={styles.btnPrimary}>
            + New Box
          </button>
          {selectedBoxId && (
            <button onClick={deleteBox} style={styles.btnDanger}>
              Delete Box
            </button>
          )}
          <button
            onClick={() => setSnapToGrid(!snapToGrid)}
            style={{
              ...styles.btnSecondary,
              backgroundColor: snapToGrid ? '#3498db' : '#ecf0f1',
              color: snapToGrid ? '#fff' : '#2c3e50',
              fontWeight: snapToGrid ? 'bold' : 'normal'
            }}
            title={`Grid Snap ${snapToGrid ? 'ON' : 'OFF'} (${GRID_SIZE}px)`}
          >
            {snapToGrid ? '⊞ Grid: ON' : '⊞ Grid: OFF'}
          </button>
        </div>
      </div>

      <div style={styles.mainContent}>
        {/* Left Sidebar - Item Library */}
        <div style={styles.sidebar}>
          <h2 style={styles.sidebarTitle}>Item Library</h2>

          {/* Add Custom Buttons */}
          <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              onClick={handleAddCustomTime}
              style={{
                ...styles.btnPrimary,
                padding: '10px 16px',
                fontSize: 13,
                backgroundColor: '#27ae60',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6
              }}
            >
              <span style={{ fontSize: 16 }}>+</span>
              Add Custom Time
            </button>
            <button
              onClick={handleAddCustomText}
              style={{
                ...styles.btnPrimary,
                padding: '10px 16px',
                fontSize: 13,
                backgroundColor: '#3498db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6
              }}
            >
              <span style={{ fontSize: 16 }}>+</span>
              Add Custom Component
            </button>
          </div>

          <div style={styles.itemCategory}>
            <h3>Zmanim</h3>
            <p>{availableItems.zmanim.length} items</p>
          </div>
          <div style={styles.itemCategory}>
            <h3>Daily Learning</h3>
            <p>{availableItems.limudim.length} items</p>
          </div>
          <div style={styles.itemCategory}>
            <h3>Jewish Calendar</h3>
            <p>{availableItems.calendar.length} items</p>
          </div>
          <div style={styles.itemCategory}>
            <h3>Custom Times</h3>
            <p>{availableItems.customTimes.length} items</p>
          </div>
          <div style={styles.itemCategory}>
            <h3>Custom Components</h3>
            <p>{availableItems.customTexts.length} items</p>
          </div>
        </div>

        {/* Center - Canvas */}
        <div style={styles.canvasWrapper}>
          <div
            ref={canvasRef}
            style={{
              ...styles.canvas,
              transform: `scale(${canvasScale})`,
              transformOrigin: 'top center',
              backgroundImage: globalSettings.backgroundType === 'image' && globalSettings.backgroundImage
                ? `url(${globalSettings.backgroundImage})`
                : 'none',
              backgroundColor: globalSettings.backgroundType === 'color'
                ? globalSettings.backgroundColor
                : '#000'
            }}
            onClick={(e) => {
              // Deselect box when clicking on canvas background (not on boxes)
              if (e.target === canvasRef.current ||
                  e.target.closest('[data-canvas-background]')) {
                setSelectedBoxId(null);
              }
            }}
          >
            {/* Header Preview */}
            <div
              data-canvas-background
              style={{
                ...styles.header,
                backgroundColor: (() => {
                  const color = globalSettings.headerBgColor;
                  const opacity = globalSettings.headerBgOpacity ?? 100;
                  if (!color || opacity === 100) return color;
                  const hex = color.startsWith('#') ? color : '#162A45';
                  const r = parseInt(hex.slice(1, 3), 16);
                  const g = parseInt(hex.slice(3, 5), 16);
                  const b = parseInt(hex.slice(5, 7), 16);
                  return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
                })(),
                color: globalSettings.headerTextColor
              }}
            >
              <span>Wednesday, January 15, 2025</span>
              <span style={{ fontSize: 28, fontWeight: 'bold' }}>12:45 PM</span>
              <span>י״ד שבט תשפ״ה</span>
            </div>

            {/* Content Area */}
            <div style={styles.content} data-canvas-background>
              {boxes.map(box => (
                <Box
                  key={box.id}
                  box={box}
                  selected={selectedBoxId === box.id}
                  onMouseDown={handleMouseDown}
                  onRemoveItem={removeItem}
                  onMoveItem={(fromIdx, toIdx) => moveItem(box.id, fromIdx, toIdx)}
                  onOpenItemModal={() => setShowItemModal(true)}
                />
              ))}
            </div>

            {/* Footer Preview */}
            <div
              data-canvas-background
              style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              padding: '10px',
              textAlign: 'center',
              fontSize: '14px',
              color: globalSettings.headerTextColor,
              backgroundColor: globalSettings.headerBgColor,
              borderTop: `2px solid ${globalSettings.boxesOutlineColor}`,
              pointerEvents: 'none',
              userSelect: 'none'
            }}>
              Powered by Shul Schedule - Get your free display at shulschedule.com
            </div>
          </div>
        </div>

        {/* Right Sidebar - Properties */}
        <div style={styles.sidebar}>
          {selectedBox ? (
            <BoxProperties
              box={selectedBox}
              onUpdateName={(name) => updateBox(selectedBoxId, { displayName: name })}
              onUpdateStyling={updateBoxStyling}
              onAddItems={() => setShowItemModal(true)}
              onRemoveItem={(itemId) => removeItem(selectedBoxId, itemId)}
              onMoveItem={(fromIdx, toIdx) => moveItem(selectedBoxId, fromIdx, toIdx)}
            />
          ) : (
            <GlobalProperties
              settings={globalSettings}
              onUpdate={(field, value) => setGlobalSettings({ ...globalSettings, [field]: value })}
            />
          )}
        </div>
      </div>

      {/* Item Selection Modal */}
      {showItemModal && selectedBox && (
        <ItemModal
          availableItems={availableItems}
          currentItems={selectedBox.items || []}
          onClose={() => setShowItemModal(false)}
          onAdd={addItemsToBox}
          onEditCustomTime={handleEditCustomTime}
          onEditCustomText={handleEditCustomText}
          onDeleteCustomTime={handleDeleteCustomTime}
          onDeleteCustomText={handleDeleteCustomText}
        />
      )}

      {/* Custom Time/Text Modal */}
      {isCustomModal && (
        <CustomModal
          isOpen={isCustomModal}
          onClose={handleCancelCustomEdit}
          title={
            customModalType === 'time'
              ? (editingCustomTime ? 'Edit Custom Time' : 'Create Custom Time')
              : (editingCustomText ? 'Edit Custom Component' : 'Create Custom Component')
          }
        >
          {customModalType === 'time' ? (
            <CustomTimes
              onCustomTimeCreated={handleCustomTimeCreated}
              editingCustomTime={editingCustomTime}
              onCancelEdit={handleCancelCustomEdit}
            />
          ) : (
            <CustomText
              onCustomTextCreated={handleCustomTextCreated}
              editingCustomText={editingCustomText}
              onCancelEdit={handleCancelCustomEdit}
            />
          )}
        </CustomModal>
      )}
    </div>
  );
};

// Box Component
const Box = ({ box, selected, onMouseDown, onRemoveItem, onMoveItem, onOpenItemModal }) => {
  const [draggedItemIndex, setDraggedItemIndex] = React.useState(null);
  const [dragOverIndex, setDragOverIndex] = React.useState(null);

  const handleItemDragStart = (e, index) => {
    e.stopPropagation();
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Prevent the box from being dragged
    e.dataTransfer.setData('text/plain', '');
  };

  const handleItemMouseDown = (e) => {
    // Prevent box movement when clicking on items
    e.stopPropagation();
  };

  const handleItemDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedItemIndex !== null && draggedItemIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleItemDrop = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedItemIndex !== null && draggedItemIndex !== index) {
      onMoveItem(draggedItemIndex, index);
    }
    setDraggedItemIndex(null);
    setDragOverIndex(null);
  };

  const handleItemDragEnd = () => {
    setDraggedItemIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div
      onMouseDown={(e) => onMouseDown(e, box.id, 'move')}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        left: box.position.x,
        top: box.position.y,
        width: box.position.width,
        height: box.position.height,
        border: box.styling.showBorder ? `3px solid ${box.styling.borderColor}` : 'none',
        backgroundColor: (() => {
          const color = box.styling.backgroundColor;
          const opacity = box.styling.backgroundOpacity ?? 100;
          if (!color || color === 'transparent' || opacity === 100) return color;
          const hex = color.startsWith('#') ? color : '#000000';
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
        })(),
        borderRadius: 12,
        padding: 16,
        cursor: 'move',
        outline: selected ? '3px solid #3498db' : (box.isMemorialBox ? '2px dashed #9b59b6' : 'none'),
        outlineOffset: 2,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        // Add background image if enabled
        ...(box.styling.useBackgroundImage && box.styling.backgroundImage && {
          backgroundImage: `url(${box.styling.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative'
        }),
        // Add subtle background indicator for memorial boxes
        ...(box.isMemorialBox && !selected ? { boxShadow: '0 0 15px rgba(155, 89, 182, 0.3)' } : {})
      }}
    >
      {/* Background Image Overlay for Opacity Control */}
      {box.styling.useBackgroundImage && box.styling.backgroundImage && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 12,
          backgroundImage: `url(${box.styling.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: (box.styling.backgroundImageOpacity || 100) / 100,
          pointerEvents: 'none',
          zIndex: 0
        }} />
      )}

      {/* Content wrapper with relative positioning to stay above background */}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Title */}
      {box.styling.showTitle !== false && (() => {
        const isBoxed = box.styling.titleBoxed;
        const verticalOffset = box.styling.titleVerticalOffset || 0;

        const titleStyle = {
          fontFamily: box.styling.titleFont,
          color: box.styling.titleColor,
          fontSize: box.styling.titleSize,
          fontWeight: box.styling.titleBold ? 'bold' : 'normal',
          textAlign: box.styling.titleAlign || 'center',
          padding: isBoxed ? '8px 16px' : '0',
          marginBottom: 20,
          marginTop: verticalOffset,
          ...(isBoxed && {
            border: `2px solid ${box.styling.titleBorderColor || box.styling.borderColor || '#d4af37'}`,
            backgroundColor: box.styling.titleBackgroundColor || box.styling.backgroundColor || 'transparent',
            borderRadius: '8px',
          })
        };

        return <div style={titleStyle}>{box.displayName}</div>;
      })()}

      {/* Items */}
      <div
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (!box.isMemorialBox) {
            onOpenItemModal();
          }
        }}
        style={{
          flex: 1,
          overflow: 'auto',
          fontFamily: box.styling.textFont,
          color: box.styling.textColor,
          fontSize: box.styling.textSize,
          cursor: 'default',
          userSelect: 'none'
        }}
      >
        {box.isMemorialBox ? (
          <div style={{ textAlign: 'center', color: '#9b59b6', fontSize: 12, marginTop: 20, fontStyle: 'italic' }}>
            Memorial Box
            <div style={{ fontSize: 10, marginTop: 8 }}>
              Content from Shul Settings: {box.memorialType === 'ilui' ? 'Ilui Nishmat' : 'Refuah Shleima'} list
            </div>
          </div>
        ) : box.items.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#95a5a6', fontSize: 14, marginTop: 20 }}>
            Double-click to add content
          </div>
        ) : (
          box.items.map((item, idx) => (
            <div
              key={idx}
              draggable={selected}
              onMouseDown={handleItemMouseDown}
              onDragStart={(e) => handleItemDragStart(e, idx)}
              onDragOver={(e) => handleItemDragOver(e, idx)}
              onDrop={(e) => handleItemDrop(e, idx)}
              onDragEnd={handleItemDragEnd}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 4,
                marginBottom: 4,
                backgroundColor: draggedItemIndex === idx
                  ? 'rgba(52, 152, 219, 0.3)'
                  : dragOverIndex === idx
                    ? 'rgba(52, 152, 219, 0.2)'
                    : 'rgba(255,255,255,0.05)',
                borderRadius: 4,
                cursor: selected ? 'grab' : 'default',
                border: dragOverIndex === idx ? '2px dashed #3498db' : '2px solid transparent',
                transition: 'background-color 0.15s, border 0.15s',
                opacity: draggedItemIndex === idx ? 0.5 : 1
              }}
            >
              {selected && (
                <span style={{
                  fontSize: 12,
                  color: '#95a5a6',
                  marginRight: 8,
                  cursor: 'grab'
                }}>
                  ⋮⋮
                </span>
              )}
              <span style={{ fontSize: 12, flex: 1 }}>{item.name || item.id}</span>
            </div>
          ))
        )}
      </div>

      {/* Resize Handles */}
      {selected && (
        <>
          {['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'].map(dir => (
            <div
              key={dir}
              onMouseDown={(e) => onMouseDown(e, box.id, 'resize', dir)}
              style={{
                position: 'absolute',
                backgroundColor: '#3498db',
                border: '2px solid white',
                ...(dir.length === 2 ? {
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  ...(dir === 'nw' && { top: -6, left: -6, cursor: 'nw-resize' }),
                  ...(dir === 'ne' && { top: -6, right: -6, cursor: 'ne-resize' }),
                  ...(dir === 'sw' && { bottom: -6, left: -6, cursor: 'sw-resize' }),
                  ...(dir === 'se' && { bottom: -6, right: -6, cursor: 'se-resize' })
                } : {
                  borderRadius: 6,
                  ...(dir === 'n' && { top: -6, left: '50%', transform: 'translateX(-50%)', width: 40, height: 12, cursor: 'n-resize' }),
                  ...(dir === 's' && { bottom: -6, left: '50%', transform: 'translateX(-50%)', width: 40, height: 12, cursor: 's-resize' }),
                  ...(dir === 'e' && { top: '50%', right: -6, transform: 'translateY(-50%)', width: 12, height: 40, cursor: 'e-resize' }),
                  ...(dir === 'w' && { top: '50%', left: -6, transform: 'translateY(-50%)', width: 12, height: 40, cursor: 'w-resize' })
                })
              }}
            />
          ))}
        </>
      )}
      </div>
    </div>
  );
};

// Box Properties Panel
const BoxProperties = ({ box, onUpdateName, onUpdateStyling, onAddItems, onRemoveItem, onMoveItem }) => {
  return (
    <div>
      <h2 style={styles.sidebarTitle}>Box Settings</h2>

      {box.isMemorialBox && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: 8,
          padding: 12,
          marginBottom: 16,
          fontSize: 13,
          lineHeight: 1.5,
          color: '#856404'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 6 }}>ℹ️ Memorial Box Notice</div>
          <div>
            Design controls are enabled to help you match this box with your theme.
            Please note: Making memorial boxes invisible (by changing colors/sizes to hide content)
            may result in account suspension.
          </div>
          <div style={{ marginTop: 8, fontSize: 12, fontStyle: 'italic' }}>
            This is the only restriction on our otherwise completely free software.
          </div>
        </div>
      )}

      <div style={styles.formGroup}>
        <label style={styles.label}>Box Name</label>
        <input
          type="text"
          value={box.displayName}
          onChange={(e) => onUpdateName(e.target.value)}
          style={styles.input}
        />
      </div>

      <div style={styles.formGroup}>
        <h3 style={styles.subtitle}>Position & Size</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
          <div><label>X:</label> {box.position.x}px</div>
          <div><label>Y:</label> {box.position.y}px</div>
          <div><label>W:</label> {box.position.width}px</div>
          <div><label>H:</label> {box.position.height}px</div>
        </div>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={box.styling.showBorder}
            onChange={(e) => onUpdateStyling('showBorder', e.target.checked)}
          />
          Show Border
        </label>
        {box.styling.showBorder && (
          <input
            type="color"
            value={box.styling.borderColor}
            onChange={(e) => onUpdateStyling('borderColor', e.target.value)}
            style={styles.colorInput}
          />
        )}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Background</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input
            type="color"
            value={box.styling.backgroundColor || '#000000'}
            onChange={(e) => onUpdateStyling('backgroundColor', e.target.value)}
            style={styles.colorInput}
          />
          <button
            onClick={() => onUpdateStyling('backgroundColor', 'transparent')}
            style={styles.btnSmall}
          >
            Transparent
          </button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={styles.label}>
            Background Opacity: {box.styling.backgroundOpacity ?? 100}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={box.styling.backgroundOpacity ?? 100}
            onChange={(e) => onUpdateStyling('backgroundOpacity', parseInt(e.target.value))}
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>

        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={box.styling.useBackgroundImage || false}
            onChange={(e) => onUpdateStyling('useBackgroundImage', e.target.checked)}
          />
          Use Background Image
        </label>

        {box.styling.useBackgroundImage && (
          <>
            <label style={{
              display: 'block',
              marginTop: 8,
              padding: '10px 16px',
              backgroundColor: '#3498db',
              color: '#fff',
              textAlign: 'center',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              transition: 'background-color 0.2s',
              ':hover': { backgroundColor: '#2980b9' }
            }}>
              📁 Choose Image File
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      onUpdateStyling('backgroundImage', event.target.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                style={{ display: 'none' }}
              />
            </label>
            {box.styling.backgroundImage && (
              <>
                <div style={{
                  marginTop: 8,
                  fontSize: 12,
                  color: '#27ae60',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}>
                  ✓ Image uploaded successfully
                </div>
                <button
                  onClick={() => onUpdateStyling('backgroundImage', null)}
                  style={{
                    ...styles.btnSmall,
                    marginTop: 8,
                    backgroundColor: '#e74c3c',
                    color: '#fff',
                    width: '100%',
                    padding: '8px 12px',
                    fontWeight: 500
                  }}
                >
                  🗑️ Remove Image
                </button>
                <label style={{ ...styles.label, marginTop: 12 }}>Image Opacity</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={(box.styling.backgroundImageOpacity || 100)}
                  onChange={(e) => onUpdateStyling('backgroundImageOpacity', parseInt(e.target.value))}
                  style={{ width: '100%', marginTop: 4 }}
                />
                <div style={{ fontSize: 11, color: '#7f8c8d', textAlign: 'center', marginTop: 4 }}>
                  {box.styling.backgroundImageOpacity || 100}%
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div style={styles.formGroup}>
        <h3 style={styles.subtitle}>Title Style</h3>
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={box.styling.showTitle !== false}
            onChange={(e) => onUpdateStyling('showTitle', e.target.checked)}
          />
          Show Title
        </label>

        {box.styling.showTitle !== false && (
          <>
            <label style={styles.label}>Font</label>
            <select
              value={box.styling.titleFont}
              onChange={(e) => onUpdateStyling('titleFont', e.target.value)}
              style={styles.select}
            >
              {['Arial', 'Times New Roman', 'Georgia', 'Courier New', 'Verdana', 'Helvetica'].map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>

            <label style={{ ...styles.label, marginTop: 8 }}>Alignment</label>
            <select
              value={box.styling.titleAlign || 'center'}
              onChange={(e) => onUpdateStyling('titleAlign', e.target.value)}
              style={styles.select}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>

            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <label style={{ ...styles.label, fontSize: 11 }}>Color</label>
                <input
                  type="color"
                  value={box.styling.titleColor}
                  onChange={(e) => onUpdateStyling('titleColor', e.target.value)}
                  style={styles.colorInput}
                />
              </div>
              <div style={{ width: 60 }}>
                <label style={{ ...styles.label, fontSize: 11 }}>Size</label>
                <input
                  type="number"
                  value={box.styling.titleSize}
                  onChange={(e) => onUpdateStyling('titleSize', parseInt(e.target.value))}
                  min="8"
                  max="72"
                  style={{ ...styles.input, width: 60 }}
                />
              </div>
            </div>

            <div style={{ marginTop: 8 }}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={box.styling.titleBold || false}
                  onChange={(e) => onUpdateStyling('titleBold', e.target.checked)}
                />
                Bold
              </label>
            </div>

            <div style={{ marginTop: 8 }}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={box.styling.titleBoxed || false}
                  onChange={(e) => onUpdateStyling('titleBoxed', e.target.checked)}
                />
                Box Title (with border)
              </label>
            </div>

            {box.styling.titleBoxed && (
              <>
                <label style={{ ...styles.label, marginTop: 8 }}>
                  Title Vertical Position: {box.styling.titleVerticalOffset || 0}px
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={() => onUpdateStyling('titleVerticalOffset', (box.styling.titleVerticalOffset || 0) - 2)}
                    style={{ ...styles.btnSmall, padding: '4px 8px' }}
                    title="Move up"
                  >
                    ▲
                  </button>
                  <input
                    type="range"
                    min="-100"
                    max="50"
                    step="1"
                    value={box.styling.titleVerticalOffset || 0}
                    onChange={(e) => onUpdateStyling('titleVerticalOffset', parseInt(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <button
                    onClick={() => onUpdateStyling('titleVerticalOffset', (box.styling.titleVerticalOffset || 0) + 2)}
                    style={{ ...styles.btnSmall, padding: '4px 8px' }}
                    title="Move down"
                  >
                    ▼
                  </button>
                  <button
                    onClick={() => onUpdateStyling('titleVerticalOffset', 0)}
                    style={{ ...styles.btnSmall, padding: '4px 8px', fontSize: 10 }}
                    title="Reset to default"
                  >
                    Reset
                  </button>
                </div>
                <div style={{ fontSize: 10, color: '#95a5a6', marginTop: 4 }}>
                  Range: -100px (far above) to +50px (deep inside)
                </div>

                <div style={{ marginTop: 8 }}>
                  <label style={{ ...styles.label, fontSize: 11 }}>Border Color</label>
                  <input
                    type="color"
                    value={box.styling.titleBorderColor || box.styling.borderColor || '#d4af37'}
                    onChange={(e) => onUpdateStyling('titleBorderColor', e.target.value)}
                    style={styles.colorInput}
                  />
                </div>

                <div style={{ marginTop: 8 }}>
                  <label style={{ ...styles.label, fontSize: 11 }}>Background Color</label>
                  <input
                    type="color"
                    value={box.styling.titleBackgroundColor || box.styling.backgroundColor || '#000000'}
                    onChange={(e) => onUpdateStyling('titleBackgroundColor', e.target.value)}
                    style={styles.colorInput}
                  />
                  <button
                    onClick={() => onUpdateStyling('titleBackgroundColor', 'transparent')}
                    style={{ ...styles.btnSmall, marginTop: 4 }}
                  >
                    Transparent
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div style={styles.formGroup}>
        <h3 style={styles.subtitle}>Content Style</h3>
        <label style={styles.label}>Font</label>
        <select
          value={box.styling.textFont}
          onChange={(e) => onUpdateStyling('textFont', e.target.value)}
          style={styles.select}
        >
          {['Arial', 'Times New Roman', 'Georgia', 'Courier New', 'Verdana', 'Helvetica'].map(f => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <input
            type="color"
            value={box.styling.textColor}
            onChange={(e) => onUpdateStyling('textColor', e.target.value)}
            style={styles.colorInput}
          />
          <input
            type="number"
            value={box.styling.textSize}
            onChange={(e) => onUpdateStyling('textSize', parseInt(e.target.value))}
            min="8"
            max="72"
            style={{ ...styles.input, width: 60 }}
          />
        </div>
      </div>

      <div style={styles.formGroup}>
        <h3 style={styles.subtitle}>Next Time Highlighting</h3>
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={box.styling.highlightEnabled || false}
            onChange={(e) => onUpdateStyling('highlightEnabled', e.target.checked)}
          />
          Enable Highlighting
        </label>
        {box.styling.highlightEnabled && (
          <>
            <div style={{ marginTop: 8 }}>
              <label style={styles.label}>Highlight Type</label>
              <select
                value={box.styling.highlightType || 'glow'}
                onChange={(e) => onUpdateStyling('highlightType', e.target.value)}
                style={styles.input}
              >
                <option value="glow">Glow</option>
                <option value="color">Color</option>
              </select>
            </div>
            <div style={{ marginTop: 8 }}>
              <label style={styles.label}>Highlight Color</label>
              <input
                type="color"
                value={box.styling.highlightColor || '#00ff00'}
                onChange={(e) => onUpdateStyling('highlightColor', e.target.value)}
                style={styles.colorInput}
              />
            </div>
          </>
        )}
      </div>

      <div style={styles.formGroup}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={styles.subtitle}>Content Items ({box.items.length})</h3>
          <button onClick={onAddItems} style={styles.btnSmall}>
            + Add
          </button>
        </div>
        {box.items.length === 0 ? (
          <p style={{ fontSize: 12, color: '#95a5a6' }}>No items yet</p>
        ) : (
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {box.items.map((item, idx) => (
              <div key={idx} style={styles.itemRow}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <button
                    onClick={() => onMoveItem(idx, idx - 1)}
                    disabled={idx === 0}
                    style={styles.btnTiny}
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => onMoveItem(idx, idx + 1)}
                    disabled={idx === box.items.length - 1}
                    style={styles.btnTiny}
                  >
                    ▼
                  </button>
                </div>
                <span style={{ flex: 1, fontSize: 12 }}>{item.name || item.id}</span>
                <button
                  onClick={() => onRemoveItem(item.id)}
                  style={{ ...styles.btnTiny, backgroundColor: '#e74c3c' }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Global Properties Panel
const GlobalProperties = ({ settings, onUpdate }) => {
  return (
    <div>
      <h2 style={styles.sidebarTitle}>Global Settings</h2>

      <div style={styles.formGroup}>
        <h3 style={styles.subtitle}>Background</h3>
        <label style={styles.radioLabel}>
          <input
            type="radio"
            checked={settings.backgroundType === 'default'}
            onChange={() => onUpdate('backgroundType', 'default')}
          />
          Default (Black)
        </label>
        <label style={styles.radioLabel}>
          <input
            type="radio"
            checked={settings.backgroundType === 'color'}
            onChange={() => onUpdate('backgroundType', 'color')}
          />
          Solid Color
        </label>
        <label style={styles.radioLabel}>
          <input
            type="radio"
            checked={settings.backgroundType === 'image'}
            onChange={() => onUpdate('backgroundType', 'image')}
          />
          Custom Image
        </label>

        {settings.backgroundType === 'color' && (
          <>
            <input
              type="color"
              value={settings.backgroundColor}
              onChange={(e) => onUpdate('backgroundColor', e.target.value)}
              style={styles.colorInput}
            />
            <div style={{ marginTop: 12 }}>
              <label style={styles.label}>
                Background Opacity: {settings.backgroundOpacity ?? 100}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={settings.backgroundOpacity ?? 100}
                onChange={(e) => onUpdate('backgroundOpacity', parseInt(e.target.value))}
                style={{ width: '100%', marginTop: 4 }}
              />
            </div>
          </>
        )}

        {settings.backgroundType === 'image' && (
          <>
            <div style={{ marginTop: 12 }}>
              <label style={{ ...styles.label, marginBottom: 8 }}>Choose from Library:</label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 8,
                marginBottom: 12
              }}>
                {BACKGROUND_IMAGES.map(bg => (
                  <div
                    key={bg.id}
                    onClick={() => onUpdate('backgroundImage', bg.image)}
                    style={{
                      padding: 8,
                      border: settings.backgroundImage === bg.image ? '2px solid #3498db' : '1px solid #ddd',
                      borderRadius: 6,
                      cursor: 'pointer',
                      textAlign: 'center',
                      backgroundColor: settings.backgroundImage === bg.image ? '#e3f2fd' : '#fff',
                      transition: 'all 0.2s'
                    }}
                  >
                    {bg.thumbnail ? (
                      <img
                        src={bg.thumbnail}
                        alt={bg.name}
                        style={{
                          width: '100%',
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 4,
                          marginBottom: 4
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: 60,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 4,
                        marginBottom: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 24
                      }}>
                        🎨
                      </div>
                    )}
                    <div style={{ fontSize: 11, fontWeight: 500 }}>{bg.name}</div>
                  </div>
                ))}
              </div>
            </div>
            <label style={{
              display: 'block',
              marginTop: 8,
              padding: '10px 16px',
              backgroundColor: '#3498db',
              color: '#fff',
              textAlign: 'center',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              transition: 'background-color 0.2s'
            }}>
              📁 Or Upload Custom Image
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      onUpdate('backgroundImage', event.target.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                style={{ display: 'none' }}
              />
            </label>
            {settings.backgroundImage && (
              <>
                <div style={{
                  marginTop: 8,
                  fontSize: 12,
                  color: '#27ae60',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}>
                  ✓ Background image selected
                </div>
                <button
                  onClick={() => onUpdate('backgroundImage', null)}
                  style={{
                    ...styles.btnSmall,
                    marginTop: 8,
                    backgroundColor: '#e74c3c',
                    color: '#fff',
                    width: '100%',
                    padding: '8px 12px',
                    fontWeight: 500
                  }}
                >
                  🗑️ Remove Image
                </button>
              </>
            )}
          </>
        )}
      </div>

      <div style={styles.formGroup}>
        <h3 style={styles.subtitle}>Header Colors</h3>
        <label style={styles.label}>Background</label>
        <input
          type="color"
          value={settings.headerBgColor}
          onChange={(e) => onUpdate('headerBgColor', e.target.value)}
          style={styles.colorInput}
        />
        <div style={{ marginTop: 8, marginBottom: 12 }}>
          <label style={styles.label}>
            Background Opacity: {settings.headerBgOpacity ?? 100}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={settings.headerBgOpacity ?? 100}
            onChange={(e) => onUpdate('headerBgOpacity', parseInt(e.target.value))}
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>

        <label style={styles.label}>Text</label>
        <input
          type="color"
          value={settings.headerTextColor}
          onChange={(e) => onUpdate('headerTextColor', e.target.value)}
          style={styles.colorInput}
        />
      </div>
    </div>
  );
};

// Item Selection Modal
const ItemModal = ({
  availableItems,
  currentItems = [],
  onClose,
  onAdd,
  onEditCustomTime,
  onEditCustomText,
  onDeleteCustomTime,
  onDeleteCustomText
}) => {
  const allItems = [
    ...availableItems.zmanim,
    ...availableItems.limudim,
    ...availableItems.calendar,
    ...availableItems.customTimes,
    ...availableItems.customTexts
  ];

  // Initialize with current items already in the box
  const [selected, setSelected] = useState(() => {
    const currentItemIds = currentItems.map(item => item.id);
    return allItems.filter(item => currentItemIds.includes(item.id));
  });

  const toggle = (item) => {
    setSelected(prev =>
      prev.find(i => i.id === item.id)
        ? prev.filter(i => i.id !== item.id)
        : [...prev, item]
    );
  };

  const isCustomTime = (category) => category === 'customTimes';
  const isCustomText = (category) => category === 'customTexts';

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2>Add Content Items</h2>
          <button onClick={onClose} style={styles.modalClose}>×</button>
        </div>

        <div style={styles.modalBody}>
          {Object.entries(availableItems).map(([category, items]) => (
            items.length > 0 && (
              <div key={category} style={{ marginBottom: 20 }}>
                <h3 style={styles.subtitle}>
                  {category.charAt(0).toUpperCase() + category.slice(1)} ({items.length})
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {items.map(item => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <label style={{ ...styles.checkboxLabel, flex: 1, margin: 0 }}>
                        <input
                          type="checkbox"
                          checked={!!selected.find(i => i.id === item.id)}
                          onChange={() => toggle(item)}
                        />
                        {item.name}
                      </label>
                      {(isCustomTime(category) || isCustomText(category)) && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const internalName = item.id.replace('customtime_', '').replace('customtext_', '');
                              if (isCustomTime(category)) {
                                onEditCustomTime(internalName);
                              } else {
                                onEditCustomText(internalName);
                              }
                            }}
                            style={{
                              padding: '4px 10px',
                              fontSize: '12px',
                              backgroundColor: '#f8f9fa',
                              color: '#495057',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#e9ecef';
                              e.currentTarget.style.borderColor = '#adb5bd';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                              e.currentTarget.style.borderColor = '#dee2e6';
                            }}
                            title="Edit"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const internalName = item.id.replace('customtime_', '').replace('customtext_', '');
                              if (isCustomTime(category)) {
                                onDeleteCustomTime(internalName);
                              } else {
                                onDeleteCustomText(internalName);
                              }
                            }}
                            style={{
                              padding: '4px 10px',
                              fontSize: '12px',
                              backgroundColor: '#fff5f5',
                              color: '#dc3545',
                              border: '1px solid #f8d7da',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8d7da';
                              e.currentTarget.style.borderColor = '#dc3545';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#fff5f5';
                              e.currentTarget.style.borderColor = '#f8d7da';
                            }}
                            title="Delete"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>

        <div style={styles.modalFooter}>
          <span>{selected.length} selected</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={styles.btnSecondary}>Cancel</button>
            <button
              onClick={() => onAdd(selected)}
              disabled={selected.length === 0}
              style={styles.btnPrimary}
            >
              Add ({selected.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom Modal Component (for Custom Times/Texts)
const CustomModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay}>
      <div
        style={{
          ...styles.modal,
          maxWidth: 700,
          maxHeight: '90vh'
        }}
      >
        <div style={styles.modalHeader}>
          <h2>{title}</h2>
          <button onClick={onClose} style={styles.modalClose}>×</button>
        </div>
        <div style={{
          ...styles.modalBody,
          paddingBottom: 0
        }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Styles
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#f5f7fa',
    fontFamily: 'Arial, sans-serif'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e1e8ed'
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    color: '#2c3e50',
    margin: 0,
    display: 'inline-block',
    marginRight: 16
  },
  saveStatus: {
    fontSize: 12,
    color: '#95a5a6'
  },
  toolbarButtons: {
    display: 'flex',
    gap: 12
  },
  btnPrimary: {
    padding: '10px 20px',
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600
  },
  btnSecondary: {
    padding: '10px 20px',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600
  },
  btnDanger: {
    padding: '10px 20px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600
  },
  btnSmall: {
    padding: '6px 12px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    marginTop: 8
  },
  btnTiny: {
    padding: '2px 6px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: 2,
    cursor: 'pointer',
    fontSize: 10
  },
  mainContent: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden'
  },
  sidebar: {
    width: 240,
    backgroundColor: '#fff',
    borderRight: '1px solid #e1e8ed',
    padding: 16,
    overflowY: 'auto'
  },
  sidebarTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 16,
    color: '#2c3e50'
  },
  itemCategory: {
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '1px solid #e1e8ed'
  },
  canvasWrapper: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    padding: 20,
    backgroundColor: '#ecf0f1'
  },
  canvas: {
    width: DISPLAY_WIDTH,
    height: DISPLAY_HEIGHT,
    backgroundColor: '#000',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    position: 'relative',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  },
  header: {
    height: HEADER_HEIGHT,
    padding: '0 40px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 18,
    fontWeight: 600
  },
  content: {
    position: 'relative',
    width: CONTENT_WIDTH,
    height: CONTENT_HEIGHT,
    margin: `0 ${PADDING}px ${PADDING}px ${PADDING}px`
  },
  formGroup: {
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: '1px solid #e1e8ed'
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#7f8c8d',
    marginBottom: 6
  },
  subtitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#7f8c8d',
    marginBottom: 8
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    fontSize: 14,
    border: '1px solid #e1e8ed',
    borderRadius: 6,
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    fontSize: 14,
    border: '1px solid #e1e8ed',
    borderRadius: 6,
    boxSizing: 'border-box'
  },
  colorInput: {
    width: 60,
    height: 36,
    border: '1px solid #e1e8ed',
    borderRadius: 4,
    cursor: 'pointer'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    cursor: 'pointer',
    marginBottom: 8
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    cursor: 'pointer',
    marginBottom: 6
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 8,
    backgroundColor: '#f8f9fa',
    borderRadius: 4,
    marginBottom: 4
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    maxWidth: 700,
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
  },
  modalHeader: {
    padding: 24,
    borderBottom: '1px solid #e1e8ed',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: 32,
    cursor: 'pointer',
    color: '#95a5a6',
    width: 32,
    height: 32,
    padding: 0
  },
  modalBody: {
    flex: 1,
    overflow: 'auto',
    padding: 24
  },
  modalFooter: {
    padding: 20,
    borderTop: '1px solid #e1e8ed',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  loading: {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
    color: '#2c3e50'
  }
};

export default DisplayLayoutBuilderV3;
